import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { Customer, CustomerRevenue } from '@metron/shared';
import { useCustomers } from '../hooks/useCustomers.ts';
import { useCustomerRevenue } from '../hooks/useCustomerRevenue.ts';
import { REVENUE_CAPTION, RevenueAmount } from '../components/CustomerRevenue.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { usePermissions } from '../hooks/usePermissions.ts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { Download, Search } from 'lucide-react';
import { InstalledMetersTooltip } from '../components/InstalledMetersTooltip.tsx';
import {
  CUSTOMER_TYPE_CATEGORIES,
  type CustomerTypeCategory,
  getCustomerTypeCategory,
} from '../lib/customerType.ts';
import { createLogger, Permission } from '@metron/shared';

const logger = createLogger('CustomersPage');

const UNKNOWN_TYPE_KEY = 'unknown';

type CustomerTypeFilter = CustomerTypeCategory | 'all' | typeof UNKNOWN_TYPE_KEY;

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

export function CustomersPage() {
  const { customers, isLoading, error } = useCustomers();
  const { getAccessToken } = useAuth();
  const { hasPermission } = usePermissions();
  const canExportGroups = hasPermission(Permission.MANAGE_CUSTOMER_ASSIGNMENTS);
  const canViewRevenue = hasPermission(Permission.VIEW_REVENUE);
  // Fetched separately from the customer list and joined here by customer id.
  const { data: revenueData } = useCustomerRevenue(canViewRevenue);
  const revenueById = useMemo(
    () => new Map<number, CustomerRevenue>(revenueData?.customers.map((r) => [r.customerId, r])),
    [revenueData],
  );
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<CustomerTypeFilter>('all');
  const tableState = useTableUrlState();

  const filteredCustomers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return customers.filter((customer) => {
      if (typeFilter !== 'all') {
        const category = getCustomerTypeCategory(customer);
        const matches = typeFilter === UNKNOWN_TYPE_KEY
          ? category === null
          : category === typeFilter;
        if (!matches) return false;
      }
      if (!query) return true;
      return (
        customer.id.toString().includes(query) ||
        customer.waterscopeAccountName.toLowerCase().includes(query) ||
        customer.waterscopeAccountId?.toLowerCase().includes(query) ||
        customer.salesforceId?.toLowerCase().includes(query) ||
        customer.metronId?.toLowerCase().includes(query) ||
        customer.accountType.toLowerCase().includes(query) ||
        customer.realEstateAccountType?.toLowerCase().includes(query)
      );
    });
  }, [customers, searchQuery, typeFilter]);

  const handleExportSensorGroups = async (
    e: React.MouseEvent,
    customerId: number,
  ) => {
    // Stop propagation to prevent row click navigation
    e.stopPropagation();

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const token = await getAccessToken();

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(
        `${apiUrl}/customers/${customerId}/sensor-groups/export`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Get the CSV data
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer-${customerId}-sensor-groups.csv`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error({ error }, 'Failed to export sensor groups');
      alert('Failed to export sensor groups. Please try again.');
    }
  };

  const handleRowClick = (customer: Customer) => {
    navigate(`/customers/${customer.id}`);
  };

  const columns: ColumnDef<Customer>[] = [
    {
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title='ID' />,
      cell: ({ row }) => <div className='text-center'>{row.getValue('id')}</div>,
    },
    {
      accessorKey: 'waterscopeAccountName',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Account Name' />,
      cell: ({ row }) => <div className='font-medium'>{row.getValue('waterscopeAccountName')}</div>,
    },
    {
      accessorKey: 'waterscopeAccountId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='WaterScope ID' />,
      cell: ({ row }) => {
        const value = row.getValue('waterscopeAccountId') as string | undefined;
        return value || <span className='text-muted-foreground'>N/A</span>;
      },
    },
    {
      id: 'customerType',
      accessorFn: (row) => getCustomerTypeCategory(row) ?? '',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Type' />,
      cell: ({ row }) => {
        const category = getCustomerTypeCategory(row.original);
        return category ?? <span className='text-muted-foreground'>Unknown</span>;
      },
    },
    {
      accessorKey: 'salesforceId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Salesforce ID' />,
      cell: ({ row }) => {
        const value = row.getValue('salesforceId') as string | undefined;
        return value || <span className='text-muted-foreground'>N/A</span>;
      },
    },
    {
      accessorKey: 'metronId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Metron ID' />,
      cell: ({ row }) => {
        const value = row.getValue('metronId') as string | undefined;
        return value
          ? <code className='text-xs'>{value}</code>
          : <span className='text-muted-foreground'>N/A</span>;
      },
    },
    {
      accessorKey: 'installedSensorCount',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={<InstalledMetersTooltip>Installed</InstalledMetersTooltip>}
        />
      ),
      cell: ({ row }) => {
        const value = row.getValue('installedSensorCount') as number;
        return <div className='text-center font-medium'>{value.toLocaleString()}</div>;
      },
    },
    {
      accessorKey: 'sensorCount',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Total' />,
      cell: ({ row }) => {
        const value = row.getValue('sensorCount') as number;
        return <div className='text-center font-medium'>{value.toLocaleString()}</div>;
      },
    },
    ...(canViewRevenue
      ? [{
        id: 'revenueT12m',
        // Undefined (unlinked, not computed, or not loaded) sorts last in
        // either direction, so dashes never interleave with real $0.
        accessorFn: (row) => revenueById.get(row.id)?.trailing12m ?? undefined,
        sortingFn: 'basic',
        sortUndefined: 'last',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Revenue (T12M)' />,
        cell: ({ row }) =>
          revenueData
            ? (
              <div className='text-right'>
                <RevenueAmount
                  revenue={revenueById.get(row.original.id)}
                  pick={(r) => r.trailing12m}
                  compact
                />
              </div>
            )
            : null,
      }] satisfies ColumnDef<Customer>[]
      : []),
    {
      accessorKey: 'lastSync',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Last Sync' />,
      cell: ({ row }) => (
        <span className='text-sm text-muted-foreground'>
          {formatDate(row.getValue('lastSync'))}
        </span>
      ),
    },
    ...(canExportGroups
      ? [{
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant='ghost'
            size='sm'
            onClick={(e) => handleExportSensorGroups(e, row.original.id)}
            title='Export Sensor Groups'
          >
            <Download className='h-4 w-4 mr-2' />
            Export Groups
          </Button>
        ),
      }] satisfies ColumnDef<Customer>[]
      : []),
  ];

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading customers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Customer List</CardTitle>
          <CardDescription>
            View all customers in the system. Click on a customer to view details.
            {canViewRevenue && ` Revenue: ${REVENUE_CAPTION}`}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center gap-2'>
            <div className='relative flex-1'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search customers by name, ID, or account type...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-8'
              />
            </div>
            <Select
              value={typeFilter}
              onValueChange={(value) => setTypeFilter(value as CustomerTypeFilter)}
            >
              <SelectTrigger className='w-[160px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Types</SelectItem>
                {CUSTOMER_TYPE_CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
                <SelectItem value={UNKNOWN_TYPE_KEY}>Unknown</SelectItem>
              </SelectContent>
            </Select>
            {(searchQuery || typeFilter !== 'all') && (
              <Button
                variant='ghost'
                onClick={() => {
                  setSearchQuery('');
                  setTypeFilter('all');
                }}
                size='sm'
              >
                Clear
              </Button>
            )}
          </div>

          <div className='text-sm text-muted-foreground'>
            Showing {filteredCustomers.length} of {customers.length} customers
          </div>

          <DataTable
            columns={columns}
            data={filteredCustomers}
            onRowClick={handleRowClick}
            sorting={tableState.sorting}
            onSortingChange={tableState.onSortingChange}
            pagination={tableState.pagination}
            onPaginationChange={tableState.onPaginationChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
